import { useCallback, useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import type { FormikHelpers } from 'formik';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import type { Id, UserRoleId } from '@e-socna/data-layer';

import { ConfirmBeforeExitForm } from '../../components/ConfirmBeforeExitForm';
import { FORM_LAYOUT_STYLE, FormLayout } from '../../components/FormLayout';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useUiContext } from '../../contexts/UiContext/hooks/useUiContext.hook';
import { userFriendlyErrors } from '../../utils/back-errors.util';
import type { BackError } from '../../utils/back-errors.util';

import { UserRolesFormControl } from '../UserRoles';
import { buildGlobalRoleIdsSchema } from '../UserRoles/schemas/global-role-ids.schema';
import { AlertMessages } from '../../components/AlertMessages';

export type UserGlobalRolesFormValues = Readonly<{
  _id: Id;
  globalRoleIds: readonly UserRoleId[];
}>;

interface Props {
  readonly initialValues: UserGlobalRolesFormValues;
  readonly onCancel: () => unknown;
  readonly onSubmit: (
    values: UserGlobalRolesFormValues,
    onError: (error: unknown) => unknown,
  ) => void;
}

export const UserGlobalRolesForm = ({
  initialValues,
  onCancel: handleCancel,
  onSubmit,
}: Readonly<Props>) => {
  const { ui } = useUiContext();
  const { userRoles } = useReferencesContext();
  const [formError, setFormError] = useState<readonly string[] | null>(null);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        globalRoleIds: buildGlobalRoleIdsSchema(userRoles),
      }),
    [userRoles],
  );

  const handleSubmit = useCallback(
    (
      values: UserGlobalRolesFormValues,
      { setSubmitting }: FormikHelpers<UserGlobalRolesFormValues>,
    ) => {
      setFormError(null);
      onSubmit(values, (error) => {
        setFormError(userFriendlyErrors(error as BackError));
        setSubmitting(false);
      });
    },
    [onSubmit],
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, isValid }) => (
        <Form
          // eslint-disable-next-line react/forbid-component-props
          style={FORM_LAYOUT_STYLE}
        >
          <ConfirmBeforeExitForm />
          <FormLayout
            actions={
              <Stack direction="row" spacing={2}>
                <Button
                  disabled={!isValid}
                  loading={isSubmitting || ui.isBackgroundOperationRunning}
                  size="large"
                  type="submit"
                  variant="contained"
                >
                  Enregistrer
                </Button>
                <Button onClick={handleCancel} size="large" variant="outlined">
                  Annuler
                </Button>
              </Stack>
            }
            error={
              !!formError && (
                <AlertMessages messages={formError} severity="error" />
              )
            }
            title="Rôles globaux"
          >
            <UserRolesFormControl
              fallbackRoleIds={null}
              name="globalRoleIds"
              required
              scope="global"
            />
          </FormLayout>
        </Form>
      )}
    </Formik>
  );
};
